import React, { useEffect, useRef } from "react";

// In-page reader for a hosted paper: page proofs in a modal dialog, with the
// real PDF one click away. Uses the native <dialog> so focus, Escape, and the
// backdrop come from the platform.
export default function Reader({ paper, open, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add("reader-open");
      dialog.scrollTop = 0;
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handleClose = () => {
      document.documentElement.classList.remove("reader-open");
      onClose();
    };
    const handleClick = (e) => {
      if (e.target === dialog) dialog.close();
    };
    dialog.addEventListener("close", handleClose);
    dialog.addEventListener("click", handleClick);
    return () => {
      dialog.removeEventListener("close", handleClose);
      dialog.removeEventListener("click", handleClick);
    };
  }, [onClose]);

  const pages = Array.from({ length: paper.pages }, (_, i) => i + 1);
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <dialog className="reader" ref={ref} aria-labelledby="reader-title">
      <div className="reader-frame">
        <header className="reader-bar">
          <div className="reader-meta">
            <p className="eyebrow">{paper.kind}</p>
            <h2 id="reader-title" className="reader-title">
              {paper.title}
            </h2>
            <p className="reader-byline">
              {paper.authors} · {paper.date} · {paper.pages} pages
            </p>
          </div>
          <div className="reader-actions">
            <a className="reader-action" href={paper.pdf} target="_blank" rel="noreferrer">
              Open PDF
            </a>
            <a className="reader-action" href={paper.pdf} download={paper.filename}>
              Download
            </a>
            <button type="button" className="reader-action reader-close" onClick={() => ref.current?.close()}>
              Close
            </button>
          </div>
        </header>
        <div className="reader-pages">
          {pages.map((n) => (
            <figure className="reader-page" key={n}>
              <img
                src={`${paper.dir}/p-${pad(n)}.jpg`}
                alt={`Page ${n} of ${paper.pages}`}
                width="1100"
                height="1556"
                loading={n <= 2 ? "eager" : "lazy"}
                decoding="async"
              />
              <figcaption className="eyebrow">
                {pad(n)} / {pad(paper.pages)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </dialog>
  );
}
